import { MenuItemType } from '../../components/select/select';
import { ALL_HEALTH_SYSTEMS } from './health-systems';
import { ALL_SERVICE_LINES } from './service-lines';

/**
 * The slice of the service's filter-options answer the dropdowns are built from.
 *
 * The service scopes this to the caller's own tenant or health system, so it is the only
 * legitimate source for what a user may filter on. The dropdowns must never be populated from a
 * fixed list: that would show a customer the names of other customers, and offer choices that can
 * only ever return nothing.
 */
export interface FilterOptionSource {
  healthSystems: string[];
  serviceLines: string[];
  /** Sites (tenants) the caller may filter on; absent or empty when there is nothing to choose. */
  tenants?: { code: string; name: string }[];
}

export const ALL_TENANTS: MenuItemType = {
  label: 'All Sites',
  value: ''
};

const toItems = (values: string[]): MenuItemType[] =>
  values.map(value => ({ label: value, value }));

/**
 * One choice is not a choice, and "All" would only repeat it, so the sentinel is offered only when
 * the caller can filter between two or more. Until the service has answered there is nothing to
 * offer, rather than a guess.
 */
const withAll = (all: MenuItemType, values: string[]): MenuItemType[] =>
  values.length > 1 ? [all, ...toItems(values)] : toItems(values);

export const healthSystemItems = (
  options: FilterOptionSource | null | undefined
): MenuItemType[] => withAll(ALL_HEALTH_SYSTEMS, options?.healthSystems ?? []);

export const serviceLineItems = (
  options: FilterOptionSource | null | undefined
): MenuItemType[] => withAll(ALL_SERVICE_LINES, options?.serviceLines ?? []);

/**
 * The caller's sites, as dropdown items. With several, "All Sites" comes first. With exactly one
 * there is nothing to choose, but the site is still worth naming, so it is returned alone and the
 * pill shows it read-only. With none (no tenant dimension, or the service has not answered) there
 * is nothing to show.
 */
export const tenantItems = (
  options: FilterOptionSource | null | undefined
): MenuItemType[] => {
  const tenants = options?.tenants ?? [];
  const items = tenants.map(t => ({ label: t.name, value: t.code }));
  return tenants.length > 1 ? [ALL_TENANTS, ...items] : items;
};

/**
 * The label for the current selection. With a single available option the unfiltered state (an
 * empty value) means that one option, so show it instead of "All".
 */
export const selectionLabel = (
  items: MenuItemType[],
  value: string,
  allLabel: string
): string =>
  items.find(item => item.value === value)?.label ??
  (items.length === 1 ? String(items[0].label) : value || allLabel);
